import { useState, useEffect, useRef } from "react";
import { usePlacesStore } from "../stores/placesStore";
import {
  OUTREACH_STATUS_LABELS,
  CATEGORY_LABELS,
  ENGAGEMENT_TYPE_LABELS,
  ENGAGEMENT_TYPE_ICONS,
  ENGAGEMENT_OUTCOME_LABELS,
  REFERRAL_DESTINATION_LABELS,
} from "@hsh/shared";
import type {
  Place,
  OutreachStatus,
  NormalizedCategory,
  BusinessNote,
  EngagementActivity,
  EngagementType,
  EngagementOutcome,
  ReferralDestination,
  LogEngagementInput,
} from "@hsh/shared";
import FoodHygieneRatingBadge from "./FoodHygieneRatingBadge";
import EditPlaceModal from "./EditPlaceModal";
import RecommendationsSection from "./RecommendationsSection";

interface PlacePanelProps {
  place: Place;
  onClose: () => void;
  planId?: string;
  onUpdate?: () => void | Promise<void>;
  isMobile?: boolean;
}

export default function PlacePanel({
  place,
  onClose,
  planId,
  onUpdate,
  isMobile = false,
}: PlacePanelProps) {
  const { updateOutreach, logEngagement, deleteEngagement } = usePlacesStore();
  const [isSaving, setIsSaving] = useState(false);
  const [showScoreDetails, setShowScoreDetails] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // New engagement logging state
  const [showLogActivity, setShowLogActivity] = useState(false);
  const [activityType, setActivityType] =
    useState<EngagementType>("phone_call");
  const [activityOutcome, setActivityOutcome] = useState<
    EngagementOutcome | ""
  >("");
  const [activityNotes, setActivityNotes] = useState("");
  const [activityFollowUp, setActivityFollowUp] = useState("");
  const [activityContactPerson, setActivityContactPerson] = useState("");
  const [activityDuration, setActivityDuration] = useState<number | "">("");
  const [referralDestination, setReferralDestination] = useState<
    ReferralDestination | ""
  >("");
  const [engagementToDelete, setEngagementToDelete] = useState<string | null>(
    null
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const previousPlaceIdRef = useRef<string | undefined>(place._id);

  // Update local state when place ID changes (switching to a different business)
  useEffect(() => {
    // Only scroll to top and reset form when switching to a DIFFERENT place
    if (previousPlaceIdRef.current !== place._id) {
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: "instant" });

      // Reset activity form when switching places
      setShowLogActivity(false);
      setActivityType("phone_call");
      setActivityOutcome("");
      setActivityNotes("");
      setActivityFollowUp("");
      setActivityContactPerson("");
      setActivityDuration("");
      setReferralDestination("");
    }
    previousPlaceIdRef.current = place._id;
  }, [place._id]);

  // Handle logging a new engagement activity
  const handleLogActivity = async () => {
    setIsSaving(true);
    try {
      const data: LogEngagementInput = {
        type: activityType,
        outcome: activityOutcome || undefined,
        notes: activityNotes.trim() || undefined,
        followUpDate: activityFollowUp
          ? new Date(activityFollowUp).toISOString()
          : undefined,
        contactPerson: activityContactPerson.trim() || undefined,
        duration: activityDuration ? Number(activityDuration) : undefined,
        referralDestination:
          activityType === "referral" && referralDestination
            ? referralDestination
            : undefined,
      };

      await logEngagement(place._id!, data, planId);

      // Reset form
      setShowLogActivity(false);
      setActivityType("phone_call");
      setActivityOutcome("");
      setActivityNotes("");
      setActivityFollowUp("");
      setActivityContactPerson("");
      setActivityDuration("");
      setReferralDestination("");

      if (onUpdate) {
        await onUpdate();
      }
    } catch (error) {
      console.error("Failed to log activity:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle deleting an engagement activity
  const handleDeleteEngagement = async (engagementId: string) => {
    setIsDeleting(true);
    try {
      await deleteEngagement(place._id!, engagementId);
      if (onUpdate) {
        await onUpdate();
      }
      // Only close the modal after all data has been refreshed
      setEngagementToDelete(null);
    } catch (error) {
      console.error("Failed to delete engagement:", error);
      // Close modal on error too so user can retry
      setEngagementToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    setIsDeleting(true);
    try {
      await updateOutreach(
        place._id!,
        {
          status,
          deleteNoteId: noteId,
        },
        planId
      );
      setNoteToDelete(null);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Failed to delete note:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusColor = (s: OutreachStatus) => {
    switch (s) {
      case "not_contacted":
        return "bg-gray-100 text-gray-700";
      case "contacted":
        return "bg-blue-100 text-blue-700";
      case "meeting_booked":
        return "bg-green-100 text-green-700";
      case "referred":
        return "bg-purple-100 text-purple-700";
      case "not_suitable":
        return "bg-red-100 text-red-700";
      case "follow_up":
        return "bg-yellow-100 text-yellow-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-red-600";
    if (score >= 60) return "text-orange-600";
    if (score >= 40) return "text-yellow-600";
    if (score >= 20) return "text-green-600";
    return "text-gray-600";
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div
        className={`p-4 border-b border-gray-200 ${
          isMobile ? "sticky top-0 bg-white z-10" : ""
        }`}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 pr-2">
            <h2
              className={`font-semibold text-gray-900 ${
                isMobile ? "text-xl" : "text-lg"
              }`}
            >
              {place.name}
            </h2>
            <p
              className={`text-gray-600 ${
                isMobile ? "text-base mt-1" : "text-sm"
              }`}
            >
              {place.address}
            </p>
            <p
              className={`text-gray-500 ${isMobile ? "text-base" : "text-sm"}`}
            >
              {place.postcode}, {place.town}
            </p>
          </div>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setIsEditModalOpen(true)}
              className={`text-gray-400 hover:text-gray-600 ${
                isMobile ? "p-2 -mt-2" : "p-1"
              }`}
              title="Edit Details"
            >
              <svg
                className={`${isMobile ? "w-6 h-6" : "w-5 h-5"}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>
            <button
              onClick={onClose}
              className={`text-gray-400 hover:text-gray-600 ${
                isMobile ? "p-2 -mr-2 -mt-2" : "ml-2"
              }`}
            >
              <svg
                className={`${isMobile ? "w-6 h-6" : "w-5 h-5"}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Quick Info */}
        <div className={`flex flex-wrap gap-2 mt-3`}>
          <span
            className={`px-2 py-1 rounded bg-gray-100 text-gray-700 ${
              isMobile ? "text-sm" : "text-xs"
            }`}
          >
            {CATEGORY_LABELS[place.normalizedCategory as NormalizedCategory] ||
              place.category}
          </span>
          <span
            className={`px-2 py-1 rounded ${
              isMobile ? "text-sm" : "text-xs"
            } ${getStatusColor(place.outreachStatus)}`}
          >
            {OUTREACH_STATUS_LABELS[place.outreachStatus]}
          </span>
        </div>
      </div>

      {/* Content */}
      <div
        ref={scrollContainerRef}
        className={`flex-1 overflow-auto p-4 space-y-6 ${
          isMobile ? "pb-24" : ""
        }`}
      >
        {/* Score Section */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h3
              className={`font-medium text-gray-900 ${
                isMobile ? "text-base" : ""
              }`}
            >
              Outreach Opportunity Score
            </h3>
            <span
              className={`font-bold ${getScoreColor(place.score ?? 0)} ${
                isMobile ? "text-3xl" : "text-2xl"
              }`}
            >
              {place.score ?? "—"}/100
            </span>
          </div>

          <button
            onClick={() => setShowScoreDetails(!showScoreDetails)}
            className={`text-primary-600 hover:text-primary-700 ${
              isMobile ? "text-base py-1" : "text-sm"
            }`}
          >
            {showScoreDetails ? "Hide" : "Show"} score breakdown
          </button>

          {showScoreDetails && place.scoreBreakdown && (
            <div className="mt-3 space-y-2">
              {place.scoreBreakdown.factors.map((factor, idx) => (
                <div key={idx} className={isMobile ? "text-base" : "text-sm"}>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">{factor.name}</span>
                    <span
                      className={
                        factor.contribution >= 0
                          ? "text-green-600"
                          : "text-red-600"
                      }
                    >
                      {factor.contribution >= 0 ? "+" : ""}
                      {Math.round(factor.contribution)}
                    </span>
                  </div>
                  <p
                    className={`text-gray-500 ${
                      isMobile ? "text-sm" : "text-xs"
                    }`}
                  >
                    {factor.reason}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Contact Info */}
        <div>
          <h3
            className={`font-medium text-gray-900 mb-2 ${
              isMobile ? "text-base" : ""
            }`}
          >
            Contact Information
          </h3>
          <div className={`space-y-3 ${isMobile ? "text-base" : "text-sm"}`}>
            {place.phone && (
              <div className="flex items-center gap-2">
                <svg
                  className={`text-gray-400 ${
                    isMobile ? "w-5 h-5" : "w-4 h-4"
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                <a
                  href={`tel:${place.phone}`}
                  className="text-primary-600 hover:text-primary-700"
                >
                  {place.phone}
                </a>
              </div>
            )}
            {place.email && (
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <a
                  href={`mailto:${place.email}`}
                  className="text-primary-600 hover:text-primary-700"
                >
                  {place.email}
                </a>
              </div>
            )}
            {place.website && (
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                  />
                </svg>
                <a
                  href={place.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 truncate"
                >
                  {place.website.replace(/^https?:\/\//, "")}
                </a>
              </div>
            )}
            {place.googleMapsUrl && (
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                </svg>
                <a
                  href={place.googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700"
                >
                  View on Google Maps
                </a>
              </div>
            )}
            {!place.phone &&
              !place.email &&
              !place.website &&
              !place.googleMapsUrl && (
                <p className="text-gray-500 italic">
                  No contact information available
                </p>
              )}
          </div>
        </div>

        {/* Company Information (from Companies House) */}
        {(place.companyNumber || place.companyName) && (
          <div>
            <h3
              className={`font-medium text-gray-900 mb-2 ${
                isMobile ? "text-base" : ""
              }`}
            >
              Company Information
            </h3>
            <div className={`space-y-3 ${isMobile ? "text-base" : "text-sm"}`}>
              {place.companyName && (
                <div className="flex items-start gap-2">
                  <svg
                    className={`text-gray-400 flex-shrink-0 mt-0.5 ${
                      isMobile ? "w-5 h-5" : "w-4 h-4"
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                  <div>
                    <span className="text-gray-900">{place.companyName}</span>
                    {place.companyNumber && (
                      <span className="text-gray-500 ml-1">
                        (#{place.companyNumber})
                      </span>
                    )}
                  </div>
                </div>
              )}

              {place.companyStatus && (
                <div className="flex items-center gap-2">
                  <svg
                    className={`text-gray-400 ${
                      isMobile ? "w-5 h-5" : "w-4 h-4"
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-medium ${
                      place.companyStatus === "active"
                        ? "bg-green-100 text-green-700"
                        : place.companyStatus === "dissolved"
                        ? "bg-red-100 text-red-700"
                        : "bg-yellow-100 text-yellow-700"
                    }`}
                  >
                    {place.companyStatus.charAt(0).toUpperCase() +
                      place.companyStatus.slice(1)}
                  </span>
                  {place.companyType && (
                    <span className="text-gray-500">({place.companyType})</span>
                  )}
                </div>
              )}

              {place.companyAge !== undefined && (
                <div className="flex items-center gap-2">
                  <svg
                    className={`text-gray-400 ${
                      isMobile ? "w-5 h-5" : "w-4 h-4"
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <span className="text-gray-700">
                    {place.companyAge < 1
                      ? "Less than 1 year old"
                      : `${place.companyAge} year${
                          place.companyAge === 1 ? "" : "s"
                        } old`}
                  </span>
                </div>
              )}

              {/* Financial Summary */}
              {(place.estimatedNetWorth != null ||
                place.estimatedCurrentAssets != null ||
                place.estimatedCurrentLiabilities != null) && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                    Financial Summary
                    {place.financialsDate && (
                      <span className="font-normal normal-case ml-1">
                        ({place.financialsDate})
                      </span>
                    )}
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    {place.estimatedCurrentAssets != null && (
                      <div>
                        <div className="text-xs text-gray-500">Assets</div>
                        <div className="font-medium text-gray-900">
                          £{place.estimatedCurrentAssets.toLocaleString()}
                        </div>
                      </div>
                    )}
                    {place.estimatedCurrentLiabilities != null && (
                      <div>
                        <div className="text-xs text-gray-500">Liabilities</div>
                        <div className="font-medium text-gray-900">
                          £{place.estimatedCurrentLiabilities.toLocaleString()}
                        </div>
                      </div>
                    )}
                    {place.estimatedNetWorth != null && (
                      <div className="col-span-2 pt-2 border-t border-gray-200">
                        <div className="text-xs text-gray-500">Net Worth</div>
                        <div
                          className={`font-semibold text-lg ${
                            place.estimatedNetWorth >= 0
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          {place.estimatedNetWorth >= 0 ? "" : "-"}£
                          {Math.abs(place.estimatedNetWorth).toLocaleString()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {place.companiesHouseUrl && (
                <div className="flex items-center gap-2 mt-2">
                  <svg
                    className={`text-gray-400 ${
                      isMobile ? "w-5 h-5" : "w-4 h-4"
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                  <a
                    href={place.companiesHouseUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700"
                  >
                    View on Companies House
                  </a>
                </div>
              )}

              {place.companyNumber && (
                <div className="flex items-center gap-2 mt-2">
                  <svg
                    className={`text-gray-400 ${
                      isMobile ? "w-5 h-5" : "w-4 h-4"
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                  <a
                    href={`https://companycheck.co.uk/company/${place.companyNumber}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700"
                  >
                    View on CompanyCheck
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Food Hygiene Rating */}
        {place.foodHygieneRating && (
          <div>
            <h3
              className={`font-medium text-gray-900 mb-3 ${
                isMobile ? "text-base" : ""
              }`}
            >
              Food Hygiene Rating
            </h3>
            <FoodHygieneRatingBadge
              rating={place.foodHygieneRating}
              size={isMobile ? "medium" : "small"}
              showDetails={true}
            />
          </div>
        )}

        {/* Smart Recommendations - AI-Powered */}
        <RecommendationsSection place={place} isMobile={isMobile} />

        {/* Engagement Section */}
        <div className="border-t border-gray-200 pt-4">
          {/* Current Status Summary */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-medium text-gray-900">Engagement Status</h3>
              <p className="text-sm text-gray-500">
                {place.totalEngagements || 0} interaction
                {(place.totalEngagements || 0) !== 1 ? "s" : ""} logged
                {place.referralCount
                  ? ` • ${place.referralCount} referral${
                      place.referralCount !== 1 ? "s" : ""
                    }`
                  : ""}
              </p>
            </div>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                place.outreachStatus
              )}`}
            >
              {OUTREACH_STATUS_LABELS[place.outreachStatus]}
            </span>
          </div>

          {/* Log Activity Button */}
          {!showLogActivity ? (
            <button
              onClick={() => setShowLogActivity(true)}
              className="w-full py-3 px-4 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center justify-center gap-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Log Activity
            </button>
          ) : (
            /* Log Activity Form */
            <div className="bg-gray-50 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-gray-900">Log New Activity</h4>
                <button
                  onClick={() => setShowLogActivity(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Activity Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Activity Type *
                </label>
                <select
                  value={activityType}
                  onChange={(e) =>
                    setActivityType(e.target.value as EngagementType)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {Object.entries(ENGAGEMENT_TYPE_LABELS).map(
                    ([value, label]) => (
                      <option key={value} value={value}>
                        {ENGAGEMENT_TYPE_ICONS[value as EngagementType]} {label}
                      </option>
                    )
                  )}
                </select>
              </div>

              {/* Referral Destination (only for referral type) */}
              {activityType === "referral" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Referred To *
                  </label>
                  <select
                    value={referralDestination}
                    onChange={(e) =>
                      setReferralDestination(
                        e.target.value as ReferralDestination
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Select destination...</option>
                    {Object.entries(REFERRAL_DESTINATION_LABELS).map(
                      ([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      )
                    )}
                  </select>
                </div>
              )}

              {/* Outcome */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Outcome
                </label>
                <select
                  value={activityOutcome}
                  onChange={(e) =>
                    setActivityOutcome(e.target.value as EngagementOutcome)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select outcome...</option>
                  {Object.entries(ENGAGEMENT_OUTCOME_LABELS).map(
                    ([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    )
                  )}
                </select>
              </div>

              {/* Contact Person */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Person
                </label>
                <input
                  type="text"
                  value={activityContactPerson}
                  onChange={(e) => setActivityContactPerson(e.target.value)}
                  placeholder="Name of person contacted"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Duration (for calls/meetings) */}
              {["phone_call", "meeting", "visit"].includes(activityType) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duration (minutes)
                  </label>
                  <input
                    type="number"
                    value={activityDuration}
                    onChange={(e) =>
                      setActivityDuration(
                        e.target.value ? Number(e.target.value) : ""
                      )
                    }
                    min="0"
                    max="480"
                    placeholder="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              )}

              {/* Follow-up Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Schedule Follow-up
                </label>
                <input
                  type="date"
                  value={activityFollowUp}
                  onChange={(e) => setActivityFollowUp(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={activityNotes}
                  onChange={(e) => setActivityNotes(e.target.value)}
                  rows={3}
                  placeholder="Details of the interaction..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Submit Button */}
              <button
                onClick={handleLogActivity}
                disabled={
                  isSaving ||
                  (activityType === "referral" && !referralDestination)
                }
                className="w-full py-2 px-4 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSaving ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  "Save Activity"
                )}
              </button>
            </div>
          )}

          {/* Engagement History Timeline */}
          {place.engagementLog && place.engagementLog.length > 0 && (
            <div className="mt-6">
              <h4 className="font-medium text-gray-900 mb-3">
                Activity Timeline
              </h4>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {[...place.engagementLog]
                  .reverse()
                  .map((activity: EngagementActivity) => (
                    <div
                      key={activity.id}
                      className="bg-white border border-gray-200 rounded-lg p-3 group relative"
                    >
                      <button
                        onClick={() => setEngagementToDelete(activity.id)}
                        className="absolute top-2 right-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Delete activity"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>

                      <div className="flex items-start gap-3">
                        <span className="text-xl">
                          {ENGAGEMENT_TYPE_ICONS[activity.type]}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-gray-900">
                              {ENGAGEMENT_TYPE_LABELS[activity.type]}
                            </span>
                            {activity.outcome && (
                              <span
                                className={`px-2 py-0.5 text-xs rounded-full ${
                                  activity.outcome === "positive"
                                    ? "bg-green-100 text-green-700"
                                    : activity.outcome === "negative"
                                    ? "bg-red-100 text-red-700"
                                    : activity.outcome === "no_answer"
                                    ? "bg-yellow-100 text-yellow-700"
                                    : "bg-gray-100 text-gray-700"
                                }`}
                              >
                                {ENGAGEMENT_OUTCOME_LABELS[activity.outcome]}
                              </span>
                            )}
                          </div>

                          {activity.contactPerson && (
                            <p className="text-sm text-gray-600">
                              Contact: {activity.contactPerson}
                            </p>
                          )}

                          {activity.type === "referral" &&
                            activity.referralDestination && (
                              <p className="text-sm text-purple-600">
                                →{" "}
                                {
                                  REFERRAL_DESTINATION_LABELS[
                                    activity.referralDestination
                                  ]
                                }
                              </p>
                            )}

                          {activity.duration && (
                            <p className="text-sm text-gray-500">
                              Duration: {activity.duration} min
                            </p>
                          )}

                          {activity.followUpDate && (
                            <p className="text-sm text-blue-600">
                              📅 Follow-up:{" "}
                              {new Date(
                                activity.followUpDate
                              ).toLocaleDateString("en-GB", {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              })}
                            </p>
                          )}

                          {activity.notes && (
                            <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">
                              {activity.notes}
                            </p>
                          )}

                          <p className="text-xs text-gray-400 mt-2">
                            {new Date(activity.createdAt).toLocaleDateString(
                              "en-GB",
                              {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                            {activity.createdByName &&
                              ` • ${activity.createdByName}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Legacy Notes (for backwards compatibility) */}
          {(!place.engagementLog || place.engagementLog.length === 0) &&
            ((place.notesLog && place.notesLog.length > 0) || place.notes) && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes History (Legacy)
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {place.notesLog &&
                    place.notesLog.length > 0 &&
                    [...place.notesLog].reverse().map((note: BusinessNote) => (
                      <div
                        key={note.id}
                        className="bg-gray-50 rounded-lg p-3 text-sm group relative"
                      >
                        <button
                          onClick={() => setNoteToDelete(note.id)}
                          className="absolute top-2 right-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Delete note"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                        <p className="text-gray-800 whitespace-pre-wrap pr-6">
                          {note.text}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(note.createdAt).toLocaleDateString(
                            "en-GB",
                            {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                        </p>
                      </div>
                    ))}
                  {place.notes &&
                    (!place.notesLog || place.notesLog.length === 0) && (
                      <div className="bg-gray-50 rounded-lg p-3 text-sm">
                        <p className="text-gray-800 whitespace-pre-wrap">
                          {place.notes}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Legacy note
                        </p>
                      </div>
                    )}
                </div>
              </div>
            )}

          {/* Follow-up Reminder */}
          {place.followUpDate && new Date(place.followUpDate) <= new Date() && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-yellow-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <span className="font-medium text-yellow-800">
                  Follow-up overdue!
                </span>
              </div>
              <p className="text-sm text-yellow-700 mt-1">
                Scheduled for{" "}
                {new Date(place.followUpDate).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </p>
            </div>
          )}
        </div>
      </div>

      <EditPlaceModal
        place={place}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={onUpdate}
      />

      {/* Delete Engagement Confirmation Modal */}
      {engagementToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Delete Activity
            </h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete this activity? This action cannot
              be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setEngagementToDelete(null)}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteEngagement(engagementToDelete)}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Note Confirmation Modal (legacy) */}
      {noteToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Delete Note
            </h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete this note? This action cannot be
              undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setNoteToDelete(null)}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteNote(noteToDelete)}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
